import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { useAtomValue, useSetAtom } from "jotai";

import {
  AssetType,
  sideBarWidthAtom,
  modalOriginPositionAtom,
  focusModeAssetAtom,
  previewNodeIdAtom,
  capitalizedAssetTypeDisplayAtom,
} from "@zapier/canvas-state";
import {
  PRODUCT_NAV_WIDTH,
  FOCUS_MODE_CONTAINER_RADIUS,
  FOCUS_MODE_TIMING,
} from "@zapier/canvas-constants";
import { Alert, Button, Spacer, Spinner, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import { getIconNameByAssetType } from "../../utils/getIconNameByAssetType";

import { useOpenAsset } from "../../hooks/useOpenAsset";
import { usePreloadedIframe } from "../../hooks/usePreloadedIframe";
import { CanvasCopilotIndicator } from "../../components/CanvasCopilotIndicator";

import styles from "./FocusModeView.module.css";
import RefreshAssetButton from "../RefreshAssetButton";

interface FocusModeViewProps {
  assetType: AssetType;
  title: string;
  url: string | undefined;
  assetId: string | null;
  onClose: () => void;
}

export function FocusModeView({
  title,
  url,
  assetType,
  assetId,
  onClose,
}: FocusModeViewProps) {
  const sideBarWidth = useAtomValue(sideBarWidthAtom);
  const modalOriginPosition = useAtomValue(modalOriginPositionAtom);
  const setModalOriginPosition = useSetAtom(modalOriginPositionAtom);
  const setFocusModeAsset = useSetAtom(focusModeAssetAtom);
  const previewNodeId = useAtomValue(previewNodeIdAtom);
  const [isClosing, setIsClosing] = useState(false);
  const openAsset = useOpenAsset();
  const getAssetDisplayName = useAtomValue(capitalizedAssetTypeDisplayAtom);

  const { isPreloaded, showLoader } = usePreloadedIframe(
    url,
    sideBarWidth,
    isClosing,
  );

  useEffect(() => {
    if (assetType) {
      setFocusModeAsset({ type: assetType, id: assetId });
    }
  }, [assetType, assetId, setFocusModeAsset]);

  const transformOrigin = modalOriginPosition
    ? `${modalOriginPosition.x}px ${modalOriginPosition.y}px`
    : "center center";

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      // Clean up origin position before closing
      setModalOriginPosition(null);
      onClose();
    }, FOCUS_MODE_TIMING.CLOSE_DELAY);
  }, [onClose, setModalOriginPosition]);

  const handleGoToAsset = useCallback(() => {
    openAsset({ url, assetType, location: "focus mode" });
  }, [openAsset, url, assetType]);

  const getBorderRadius = (width: number) =>
    width > PRODUCT_NAV_WIDTH ? "0px" : `${FOCUS_MODE_CONTAINER_RADIUS}px`;

  return (
    <div
      className={classNames(styles.overlay, {
        [styles.overlayClosing]: isClosing,
      })}
      style={{
        left: `${sideBarWidth}px`,
        borderTopLeftRadius: getBorderRadius(sideBarWidth),
        transformOrigin,
      }}
    >
      <div
        className={styles.container}
        data-testid="focus-mode-container"
        style={{
          borderTopLeftRadius: getBorderRadius(PRODUCT_NAV_WIDTH),
        }}
      >
        <div>
          <div
            className={styles.header}
            style={{
              borderTopLeftRadius: getBorderRadius(sideBarWidth),
            }}
          >
            <div className={styles.leftSection}>
              <Button
                variant="tertiary"
                size="small"
                iconPosition="start"
                onPress={handleClose}
                className={styles.backButton}
              >
                <Icon name="arrowBigLeft" />
                Back to Canvas
              </Button>

              <Spacer width={8} />
              <Icon
                name={getIconNameByAssetType(assetType)}
                size={16}
                color="BrandOrange"
              />
              <Spacer width={4} />
              <Text type="Body1" fontWeight={400} className={styles.titleText}>
                {title}
              </Text>
              <Spacer width={8} />
            </div>

            <div className={styles.rightSection}>
              <RefreshAssetButton isLoading={showLoader} />
              <Button
                variant="primary"
                size="small"
                onPress={handleGoToAsset}
                isDisabled={!url}
              >
                Go to {getAssetDisplayName(assetType)}
              </Button>
            </div>
          </div>
          {!showLoader && (
            <Alert variant="banner" status="info">
              <Text type="Body2" color="TextDefault">
                This preview is read-only, but Copilot can help you modify it.{" "}
                For full editing capabilities, go to the{" "}
                {getAssetDisplayName(assetType)} builder.
              </Text>
            </Alert>
          )}
        </div>

        {showLoader && url && (
          <div className={styles.loaderContainer}>
            <div className={styles.loader}>
              <Spinner size="small" />
              <Spacer height={8} />
              <Text type="Body1" color="TextWeakest">
                Loading preview...
              </Text>
            </div>
          </div>
        )}

        <CanvasCopilotIndicator
          inFocusMode
          nodeId={previewNodeId || ""}
          isClosing={isClosing}
        />

        {url && !isPreloaded && (
          <iframe
            title={title}
            src={url}
            className={classNames(styles.iframe, {
              [styles.iframeHidden]: showLoader,
            })}
            sandbox={"allow-same-origin allow-scripts"}
          />
        )}
      </div>
    </div>
  );
}
